/**
 * Semantic color tokens — canonical source for status, surface, text, and accent.
 * Values are full Tailwind class fragments so JIT can see them.
 *
 * Sprint 5 WI-1 scope: semantic palette only. Domain accents (wallet, live, …)
 * live under `domain` for gradual migration in later WIs.
 */

/** Status + surface semantics used across alerts, badges, and shells */
export const semanticColors = {
  accent: {
    bg: 'bg-emerald-500',
    bgHover: 'hover:bg-emerald-400',
    surface: 'bg-emerald-500/10',
    surfaceSubtle: 'bg-emerald-500/[0.04]',
    surfaceEmphasis: 'bg-emerald-500/15',
    border: 'border-emerald-500/30',
    borderSubtle: 'border-emerald-500/25',
    borderEmphasis: 'border-emerald-500/40',
    text: 'text-emerald-400',
    textEmphasis: 'text-emerald-300',
    onAccent: 'text-black',
    borderSolid: 'border-emerald-400',
  },
  background: {
    DEFAULT: 'bg-black',
    app: 'bg-[#0a0a0a]',
    raised: 'bg-gray-900',
    overlay: 'bg-gray-950',
    modal: 'bg-gray-900',
    scrim: 'bg-black/95',
  },
  surface: {
    DEFAULT: 'bg-gray-900/30',
    raised: 'bg-gray-900/50',
    glass: 'bg-white/5',
    subtle: 'bg-white/[0.03]',
    inset: 'bg-black/40',
    neutral: 'bg-gray-900/40',
    divider: 'bg-gray-800',
  },
  text: {
    primary: 'text-white',
    secondary: 'text-gray-400',
    muted: 'text-gray-500',
    subdued: 'text-gray-600',
    label: 'text-gray-400',
    onDark: 'text-gray-300',
  },
  border: {
    DEFAULT: 'border-gray-800',
    subtle: 'border-white/10',
    strong: 'border-gray-700',
    empty: 'border-gray-900',
  },
  success: {
    surface: 'bg-emerald-500/10',
    border: 'border-emerald-500/30',
    text: 'text-emerald-400',
  },
  warning: {
    surface: 'bg-amber-500/10',
    surfaceEmphasis: 'bg-amber-500/15',
    border: 'border-amber-500/30',
    borderEmphasis: 'border-amber-500/40',
    text: 'text-amber-200',
  },
  danger: {
    surface: 'bg-red-500/10',
    border: 'border-red-500/30',
    text: 'text-red-400',
  },
  info: {
    surface: 'bg-blue-500/10',
    border: 'border-blue-500/30',
    text: 'text-blue-200',
  },
};

/** Feature-domain accents — not part of core semantics; migrate in WI-3+ */
export const domainColors = {
  wallet: {
    surface: 'bg-purple-500/15',
    border: 'border-purple-500/30',
    borderEmphasis: 'border-purple-500/40',
    text: 'text-purple-300',
    accent: 'bg-purple-500',
  },
  live: {
    border: 'border-blue-500/30',
    accent: 'text-blue-500',
    surface: 'bg-blue-500/10',
    text: 'text-blue-200',
  },
  friends: {
    border: 'border-violet-500/30',
    accent: 'text-violet-500',
  },
  admin: {
    surface: 'bg-violet-500/10',
    surfaceEmphasis: 'bg-violet-500/15',
    border: 'border-violet-500/30',
    borderEmphasis: 'border-violet-500/40',
    text: 'text-violet-200',
    textEmphasis: 'text-violet-300',
    accent: 'bg-violet-500',
  },
  neutral: {
    surface: 'bg-white/5',
    border: 'border-white/10',
    text: 'text-gray-300',
    textMuted: 'text-gray-400',
  },
};

/**
 * Combined surface + border + text for alert-style fills.
 * @param {'success'|'warning'|'danger'|'info'|'admin'|'neutral'} tone
 */
export function semanticFill(tone) {
  if (tone === 'admin' || tone === 'neutral') {
    const d = domainColors[tone];
    return `${d.surface} ${d.border} ${d.text}`;
  }
  const c = semanticColors[tone];
  if (!c) return semanticFill('danger');
  return `${c.surface} ${c.border} ${c.text}`;
}

/** @deprecated Use semanticColors — kept for gradual import migration */
export const colors = {
  ...semanticColors,
  brand: semanticColors.accent,
  surface: {
    base: 'black',
    app: '#0a0a0a',
    raised: 'gray-900',
    overlay: 'gray-950',
    glass: 'white/[0.03]',
  },
  wallet: domainColors.wallet,
  live: domainColors.live,
  friends: domainColors.friends,
  custom: { accent: 'purple-400', border: 'purple-500/30' },
  admin: domainColors.admin,
  warning: semanticColors.warning,
  danger: semanticColors.danger,
  success: semanticColors.success,
};
